import type { LoginResult } from "../../hooks/useAuthApi";
import { isValidEmail } from "../../utils/authValidation";
import { getOrgInitials, ORG_INITIALS_FALLBACK } from "../tenantContext/tenantContextMappers";
import { resetThemeToLight } from "../../design-system/utils/theme-utils";
import { clearActiveWorkspace } from "../workspace/activeWorkspace";

/** localStorage key for the signed-in user's superadmin flag ("true" or absent). */
const SUPERADMIN_KEY = "isSuperadmin";

/** Identity keys written across the login/signup/OAuth entry points. */
const IDENTITY_KEYS = [
  "accessToken",
  "userEmail",
  "userId",
  "userName",
  "intercomToken",
  SUPERADMIN_KEY,
] as const;

const USER_NAME_KEY = "userName";

/** A name part the API left empty, or one stored as text by older builds ("null null"). */
const isBlankNamePart = (part: string): boolean => !part || /^(null|undefined)$/i.test(part);

/** The account's name from its parts, skipping missing ones: "" when it has none. */
export const joinUserName = (...parts: Array<string | null | undefined>): string =>
  parts
    .flatMap((part) => (part ?? "").trim().split(/\s+/))
    .filter((part) => !isBlankNamePart(part))
    .join(" ");

/** Store the name, or drop it when the account has none. */
export const storeUserName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): void => {
  const name = joinUserName(firstName, lastName);
  if (name) localStorage.setItem(USER_NAME_KEY, name);
  else localStorage.removeItem(USER_NAME_KEY);
};

/** The stored name, "" when there is none or storage is blocked. */
export const readStoredUserName = (): string => {
  try {
    return joinUserName(localStorage.getItem(USER_NAME_KEY));
  } catch {
    return "";
  }
};

/** Store user identity after a successful login/signup API response. */
export const persistLoginSession = (result: LoginResult): void => {
  if (result.user) {
    localStorage.setItem("userId", result.user.id);
    storeUserName(result.user.first_name, result.user.last_name);
    if (result.user.intercom_token) {
      localStorage.setItem("intercomToken", result.user.intercom_token);
    } else {
      localStorage.removeItem("intercomToken");
    }
    if (result.user.is_superadmin === true) {
      localStorage.setItem(SUPERADMIN_KEY, "true");
    } else {
      localStorage.removeItem(SUPERADMIN_KEY);
    }
  }

  // Login no longer returns tenant/workspace — the user picks a tenant on
  // /select-organization. Drop any context left by a previous session so the guard
  // cannot admit the new user into the previous user's tenant.
  clearActiveWorkspace();
};

/** Remove every stored trace of the session — used by logout and 401 recovery. */
export const clearStoredAuthSession = (): void => {
  IDENTITY_KEYS.forEach((key) => localStorage.removeItem(key));
  clearActiveWorkspace();
  resetThemeToLight();
};

/** The signed-in user as the org picker's identity row shows them. */
export interface StoredIdentity {
  email: string;
  /** From the name ("Hardik Chheda" → "HC"), else the email's local part. */
  initials: string;
}

/** "hardik.chheda@…" → "hardik chheda", so the local part splits into words. */
const emailLocalWords = (email: string): string => email.split("@")[0].replace(/[._+]+/g, " ");

/**
 * The stored identity, or `null` when there is no usable email.
 *
 * `userEmail` isn't always an email: the OAuth callback stores the token's
 * `sub` claim there, which can be an id. Only a value that passes
 * `isValidEmail` counts. Blocked storage (private mode, sandboxed frames)
 * also gives `null`, never a throw.
 */
export const readStoredIdentity = (): StoredIdentity | null => {
  try {
    const email = localStorage.getItem("userEmail")?.trim() ?? "";
    if (!isValidEmail(email)) return null;
    const name = readStoredUserName();
    const initials =
      getOrgInitials(name) || getOrgInitials(emailLocalWords(email)) || ORG_INITIALS_FALLBACK;
    return { email, initials };
  } catch {
    return null;
  }
};

/**
 * Whether the signed-in user is a platform superadmin, as stored at login.
 * Missing flag or blocked storage gives `false`, never a throw.
 */
export const readStoredIsSuperadmin = (): boolean => {
  try {
    return localStorage.getItem(SUPERADMIN_KEY) === "true";
  } catch {
    return false;
  }
};

/** Keep the stored flag in step with the backend (`/auth/me`). Blocked storage is ignored. */
export const storeIsSuperadmin = (isSuperadmin: boolean): void => {
  try {
    if (isSuperadmin) localStorage.setItem(SUPERADMIN_KEY, "true");
    else localStorage.removeItem(SUPERADMIN_KEY);
  } catch {
    // Storage unavailable: the in-memory query result still drives the UI.
  }
};

/** Who the stored session belongs to (id from password login, email/sub from OAuth). */
export const readStoredUserKey = (): string => {
  try {
    return localStorage.getItem("userId") ?? localStorage.getItem("userEmail") ?? "";
  } catch {
    return "";
  }
};
